/**
 * Which days a calendar view shows — `docs/activity.md` §6.1.
 *
 * **Sunday first** (Aaron, point 2; do-not-substitute 20): the side panel's
 * month, Week, Month and the phone's strip all start on Sunday, in every
 * company, and there is no setting for it. **Work week** is Monday to Friday.
 * One constant, so nothing can start a week anywhere else.
 */
import { addDays, monthOf, parseDay } from '../calendar.js';
import { weekdayOf } from '../requests/words.js';
/** 0 = Sunday. Week is Sunday to Saturday, so Saturday and Sunday come with Week. */
export const WEEK_STARTS_ON = 0;
export const CALENDAR_VIEWS = ['day', 'work_week', 'week', 'month'];
export const CALENDAR_VIEW_LABELS = {
    day: 'Day',
    work_week: 'Work week',
    week: 'Week',
    month: 'Month',
};
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];
const short = (word) => word.slice(0, 3);
/** The Sunday a day's week starts on. */
export function weekStartOf(day) {
    return addDays(day, -((weekdayOf(day) - WEEK_STARTS_ON + 7) % 7));
}
/** Every day from `from` to `to`, both included. */
export function daysBetween(from, to) {
    const days = [];
    for (let day = from; day <= to; day = addDays(day, 1))
        days.push(day);
    return days;
}
/**
 * The days a view shows around `day`: one; Monday to Friday of its week; its
 * Sunday-to-Saturday week; or the whole weeks that cover its month (Sunday
 * first, so Oct 2026 runs Sep 27 – Oct 31).
 */
export function viewDays(view, day) {
    if (view === 'day')
        return [day];
    const sunday = weekStartOf(day);
    if (view === 'work_week')
        return daysBetween(addDays(sunday, 1), addDays(sunday, 5));
    if (view === 'week')
        return daysBetween(sunday, addDays(sunday, 6));
    const { start, end } = monthOf(day);
    const first = weekStartOf(start);
    const last = addDays(weekStartOf(end), 6);
    return daysBetween(first, last);
}
/** The month as weeks of seven days, Sunday first — Month and the side panel. */
export function monthWeeks(day) {
    const days = viewDays('month', day);
    const weeks = [];
    for (let index = 0; index < days.length; index += 7)
        weeks.push(days.slice(index, index + 7));
    return weeks;
}
/** Where ‹ and › go from `day` in a view. */
export function stepView(view, day, direction) {
    if (view === 'day')
        return addDays(day, direction);
    if (view === 'month') {
        const { year, month } = parseDay(day);
        const target = new Date(Date.UTC(year, month - 1 + direction, 1));
        return target.toISOString().slice(0, 10);
    }
    return addDays(day, 7 * direction);
}
/**
 * Where an arrow key takes the selected day (Outlook's, Aaron Oct 23): ← and
 * → a day, ↑ and ↓ a week in Month. Work week steps over the weekend, so the
 * day it lands on is always one the board shows. Null for any other key.
 */
export function arrowDay(view, day, key) {
    const step = key === 'ArrowLeft' ? -1 : key === 'ArrowRight' ? 1
        : view === 'month' && key === 'ArrowUp' ? -7 : view === 'month' && key === 'ArrowDown' ? 7 : 0;
    if (step === 0)
        return null;
    let next = addDays(day, step);
    if (view === 'work_week') {
        while (weekdayOf(next) === 0 || weekdayOf(next) === 6)
            next = addDays(next, Math.sign(step));
    }
    return next;
}
/**
 * The toolbar's range, never wrapping (§6.1): *Thursday, October 22* ·
 * *Oct 19 – 23, 2026* · *Oct 18 – 24, 2026* · *October 2026*; a range across
 * two months names both (*Oct 28 – Nov 3, 2026*), across two years both years.
 */
export function rangeLabel(view, day) {
    const parts = parseDay(day);
    if (view === 'day') {
        const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return `${names[weekdayOf(day)]}, ${MONTHS[parts.month - 1]} ${parts.day}`;
    }
    if (view === 'month')
        return `${MONTHS[parts.month - 1]} ${parts.year}`;
    const days = viewDays(view, day);
    const first = parseDay(days[0]);
    const last = parseDay(days[days.length - 1]);
    const firstMonth = short(MONTHS[first.month - 1]);
    const lastMonth = short(MONTHS[last.month - 1]);
    if (first.year !== last.year) {
        return `${firstMonth} ${first.day}, ${first.year} – ${lastMonth} ${last.day}, ${last.year}`;
    }
    if (first.month !== last.month)
        return `${firstMonth} ${first.day} – ${lastMonth} ${last.day}, ${last.year}`;
    return `${firstMonth} ${first.day} – ${last.day}, ${last.year}`;
}
/** The side panel's month heading: *October 2026*. */
export function monthHeading(day) {
    const parts = parseDay(day);
    return `${MONTHS[parts.month - 1]} ${parts.year}`;
}
/** *S M T W T F S* — the side panel's month and the phone's strip. */
export const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
//# sourceMappingURL=week.js.map